import React from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import IACompositionBar from 'pages/InAction/components/IACompositionBar';

export interface AnalysisPreviewTotals {
  total: number;
  learned: number;
  notLearned: number;
  outOfVocab: number;
  needsAttention: number;
  needsRepeat: number;
}

interface AnalysisPreviewProps {
  totals: AnalysisPreviewTotals;
  title: string;
  saving: boolean;
  onTitleChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onSaveAndLearn?: () => void;
}

const AnalysisPreview: React.FC<AnalysisPreviewProps> = ({
  totals,
  title,
  saving,
  onTitleChange,
  onSave,
  onCancel,
  onSaveAndLearn,
}) => {
  const comp = {
    learned: totals.learned,
    repeat: totals.needsRepeat,
    notLearned: totals.notLearned,
    new: totals.outOfVocab,
    attention: totals.needsAttention,
  };
  const newPct = totals.total
    ? Math.round((totals.outOfVocab / totals.total) * 100)
    : 0;
  const showTip = newPct >= 20;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        boxShadow: '0 14px 36px -28px rgba(20,20,20,0.4)',
        p: { xs: 2.5, md: '26px 28px' },
      }}
    >
      <Stack direction={'row'} alignItems={'center'} spacing={1.75} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: (t) =>
              t.palette.mode === 'light'
                ? 'rgba(59,44,147,0.10)'
                : 'rgba(155,124,255,0.18)',
            borderRadius: '50%',
            color: 'primary.main',
            display: 'inline-flex',
            flex: 'none',
            height: 46,
            justifyContent: 'center',
            width: 46,
          }}
        >
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 26 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15 }}>
            {'Аналіз готовий'}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {`Знайдено ${totals.total} унікальних слів — ось як вони лягають на твій словник`}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mb: 2.5 }}>
        <IACompositionBar comp={comp} height={18} legend />
      </Box>

      {showTip && (
        <Stack
          direction={'row'}
          spacing={1.25}
          alignItems={'flex-start'}
          sx={{
            bgcolor: 'rgba(255,193,7,0.10)',
            border: '1px solid rgba(255,193,7,0.40)',
            borderRadius: 2,
            mb: 2.5,
            px: 2,
            py: 1.5,
          }}
        >
          <TipsAndUpdatesOutlinedIcon
            sx={{ color: '#A87800', flex: 'none', fontSize: 20, mt: '2px' }}
          />
          <Typography sx={{ fontSize: 14, lineHeight: 1.45 }}>
            <Box component={'b'} sx={{ fontWeight: 800 }}>
              {`${newPct}% слів — нові.`}
            </Box>
            {
              ' Гарний матеріал, щоб підтягнути словник. Збережи список і додай їх у вправи.'
            }
          </Typography>
        </Stack>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: 'text.secondary',
              display: 'block',
              fontSize: 13,
              fontWeight: 700,
              mb: 0.75,
            }}
          >
            {'Назва списку'}
          </Typography>
          <TextField
            fullWidth
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder={'Напр. "GoT S04E03" або "1984 ch.5"'}
            sx={{
              '& .MuiInputBase-root': {
                bgcolor: 'background.default',
                fontSize: 15,
                height: 44,
              },
            }}
          />
        </Box>
        <Button
          variant={'text'}
          startIcon={<ArrowBackIcon />}
          onClick={onCancel}
          disabled={saving}
          sx={{ height: 44, px: 2 }}
        >
          {'Назад'}
        </Button>
        <Button
          variant={'outlined'}
          startIcon={<BookmarkAddOutlinedIcon />}
          onClick={onSave}
          disabled={saving}
          sx={{ height: 44, px: 2 }}
        >
          {'Зберегти'}
        </Button>
        <Button
          loading={saving}
          onClick={onSaveAndLearn ?? onSave}
          variant={'contained'}
          startIcon={<SchoolOutlinedIcon />}
          sx={{ height: 44, px: 2 }}
        >
          {'Зберегти і вчити'}
        </Button>
      </Stack>
    </Box>
  );
};

export default AnalysisPreview;
